import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Grid, Heading, Text } from '@radix-ui/themes';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { FilledCircleIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTrainingStatusIcon } from 'components/common/pitch-lists/training-status-icon';
import { CommonTable } from 'components/common/table';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { LOCAL_TIMEZONE } from 'enums/env';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { TABLES } from 'enums/tables';
import { TableIdentifier } from 'interfaces/cookies/i-app.cookie';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_SPIN_DELTA_RPM,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine, IMachineModelDictionary } from 'lib_ts/interfaces/i-machine';
import { IGroupedFires } from 'lib_ts/interfaces/i-pitch-performance';
import { debounce, orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MachinesService } from 'services/machines.service';

const COMPONENT_NAME = 'PitchPerfGroupTable';

const IDENTIFIER = TableIdentifier.PitchPerformanceGroupList;
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface IProps {
  machine: IMachine;
  onDrillDown: (id: string) => void;
}

export const PitchPerfGroupTable = ({ machine, onDrillDown }: IProps) => {
  const { loading, allGroups, filters, aggDict } = usePitchPerformanceStore();

  const [search, setSearch] = useState('');
  const [safeSearch, setSafeSearch] = useState(search);
  const [pitchTypeFilter, setPitchTypeFilter] = useState<PitchType>();

  const [modelsDict, setModelsDict] = useState<IMachineModelDictionary>({});

  // required for showing names of models used to build each ms
  useEffect(() => {
    MachinesService.getInstance()
      .getModelsDictionary()
      .then((dict) => setModelsDict(dict));
  }, []);

  const onSearch = useCallback(
    debounce((value: string) => {
      setSafeSearch(value.trim().toLowerCase());
    }, 500),
    []
  );

  useEffect(() => {
    onSearch(search);
  }, [search, onSearch]);

  const searchGroups = useMemo(() => {
    if (!safeSearch && !pitchTypeFilter) {
      return allGroups;
    }

    return allGroups
      .filter((g) => !safeSearch || g.name.toLowerCase().includes(safeSearch)) // matches search query
      .filter((g) => !pitchTypeFilter || g.type === pitchTypeFilter); // matches pitch type
  }, [allGroups, safeSearch, pitchTypeFilter]);

  const pagination: ITablePageable = {
    identifier: IDENTIFIER,
    total: searchGroups.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'name',
      dir: -1,
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    // just a placeholder
    checkedActions: () => [],
  };

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  const columns: ITableColumn[] = [
    {
      label: 'Pitch Name',
      key: 'name',

      formatFn: (m: IGroupedFires) => {
        const lastFired = orderBy(
          m.fires,
          ({ shot }) => shot._created,
          'desc'
        )[0];

        return (
          <Flex gap="2">
            <Text style={{ color: `rgb(${m.rgb})` }}>
              <FilledCircleIcon />
            </Text>
            <Flex direction="column" gap="1">
              <Box minWidth="0">
                <Text truncate>
                  {m.name} {m.type}
                </Text>
              </Box>
              <Text size="1" color={RADIX.COLOR.NEUTRAL}>
                {format(
                  parseISO(lastFired.shot._created),
                  'MM/dd/yyyy HH:mm z',
                  {
                    timeZone: LOCAL_TIMEZONE,
                  }
                )}
              </Text>
            </Flex>
          </Flex>
        );
      },
    },
    {
      label: 'Status',
      key: '_status',
      disableSort: true,
      align: 'center',
      tooltipFn: (m: IGroupedFires) => {
        const { ms: targetMs } = m.target;

        return PitchListHelper.getTrainedStatusTooltip({
          ms: targetMs,
          aggShots: aggDict[targetMs.matching_hash ?? ''],
          threshold: machine.training_threshold,
          modelsDict: modelsDict,
        });
      },
      formatFn: (m: IGroupedFires) => {
        return (
          <CommonTrainingStatusIcon
            aggShots={aggDict[m.target.ms.matching_hash ?? '']}
            threshold={machine.training_threshold}
            precision={!!m.target.ms.precision_trained}
            autoRefresh={false}
            canRefresh={false}
          />
        );
      },
    },
    {
      label: 'Shots',
      key: '_shots',
      align: 'center',
      classNameFn: () => 'vertical-align-top',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.fires.length > b.fires.length ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex
          display="inline-flex"
          className="cursor-pointer"
          gap="1"
          justify="center"
          p="2"
          onClick={() => onDrillDown(m._id)}
        >
          <Text
            color={RADIX.COLOR.ACCENT}
            style={{ textDecoration: 'underline' }}
          >
            {m.fires.length}
          </Text>
          <Text color={RADIX.COLOR.ACCENT}>&gt;</Text>
        </Flex>
      ),
    },
    {
      label: 'common.speed',
      subLabel: 'mph',
      key: '_speed',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.speedMPH > b.average.speedMPH ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{m.average.speedMPH.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {m.target.speedMPH.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.speedMPH) > MAX_VELO_DELTA_MPH
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.speedMPH > 0 && '+'}
              {m.delta.speedMPH.toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.spin-rate',
      subLabel: 'rpm',
      key: '_netSpin',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.netSpin > b.average.netSpin ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{m.average.netSpin.toFixed(0)}</Text>
          </Box>

          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {m.target.netSpin.toFixed(0)}
            </Text>
          </Box>

          <Box>
            <Badge
              color={
                Math.abs(m.delta.netSpin) > MAX_SPIN_DELTA_RPM
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.netSpin > 0 && '+'}
              {m.delta.netSpin.toFixed(0)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.h-break',
      subLabel: 'in',
      key: '_breakX',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.break.xInches > b.average.break.xInches ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{-m.average.break.xInches.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {-m.target.break.xInches.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.break.xInches) > MAX_BREAKS_DELTA_INCHES
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {-m.delta.break.xInches > 0 && '+'}
              {-m.delta.break.xInches.toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.v-break',
      subLabel: 'in',
      key: '_breakZ',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.break.zInches > b.average.break.zInches ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{m.average.break.zInches.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {m.target.break.zInches.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.break.zInches) > MAX_BREAKS_DELTA_INCHES
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.break.zInches > 0 && '+'}
              {m.delta.break.zInches.toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.plate-x',
      subLabel: 'in',
      key: '_plateX',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.plate.plate_x > b.average.plate.plate_x ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{(m.average.plate.plate_x * FT_TO_INCHES).toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {(m.target.plate.plate_x * FT_TO_INCHES).toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.plate.plate_x * FT_TO_INCHES) >
                MAX_PLATE_DELTA_IN
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.plate.plate_x > 0 && '+'}
              {(m.delta.plate.plate_x * FT_TO_INCHES).toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.plate-z',
      subLabel: 'in',
      key: '_plateZ',
      sortRowsFn: (a: IGroupedFires, b: IGroupedFires, dir) =>
        (a.average.plate.plate_z > b.average.plate.plate_z ? 1 : -1) * dir,
      formatFn: (m: IGroupedFires) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{(m.average.plate.plate_z * FT_TO_INCHES).toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {(m.target.plate.plate_z * FT_TO_INCHES).toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.plate.plate_z * FT_TO_INCHES) >
                MAX_PLATE_DELTA_IN
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.plate.plate_z > 0 && '+'}
              {(m.delta.plate.plate_z * FT_TO_INCHES).toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
  ];

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Box>
        <CommonTable
          id={COMPONENT_NAME}
          toolbarContent={
            <Grid columns="2" gap={RADIX.FLEX.GAP.MD}>
              <Box>
                <Flex align="center" gap="2">
                  <Heading size="4">Pitches</Heading>
                  <Badge color={RADIX.COLOR.SECONDARY}>
                    {allGroups.length} Pitches
                  </Badge>
                </Flex>
                <Text color={RADIX.COLOR.SECONDARY}>
                  Details for each BP pitch type with mean error values
                </Text>
              </Box>
              <Grid columns="2" gap={RADIX.FLEX.GAP.SM} align="end">
                <CommonTextInput
                  id="search-groups"
                  placeholder="Search Pitch by Name"
                  value={search}
                  onChange={(v) => setSearch(v ?? '')}
                  rightSlot={<MagnifyingGlassIcon />}
                  disabled={loading}
                />
                <CommonSearchInput
                  id="pitch-list-search-type"
                  name="type"
                  placeholder="common.pitch-type"
                  options={PITCH_TYPE_FILTER_OPTIONS}
                  values={pitchTypeFilter ? [pitchTypeFilter] : []}
                  onChange={(v) => setPitchTypeFilter(v[0] as PitchType)}
                  disabled={loading}
                  optional
                  skipSort
                />
              </Grid>
            </Grid>
          }
          displayColumns={columns}
          displayData={searchGroups}
          loading={loading}
          {...pagination}
          {...checkable}
          {...sort}
        />
      </Box>
    </ErrorBoundary>
  );
};
